/**
 * AuthenticationService
 *
 * Purpose
 * - Centralizes authentication concerns (login, logout, token handling) for the Angular client.
 * - Stores and rehydrates the OAuth bearer token and user profile across page refreshes (sessionStorage).
 * - Exposes session state via observables (isUserLoggedIn$, myUser$) for components and guards to react to.
 * - Builds Authorization headers (Bearer <token>) for API calls when needed.
 *
 * Usage
 * - login(form): POSTs username/password to /Token (password grant). On success, call setToken(response)
 *   and then load the user profile as needed. The AuthInterceptor will automatically attach the token to API calls.
 * - logout(): POSTs /api/Account/Logout with the current bearer token, clears sessionStorage and navigates to /login.
 * - ping(): POSTs /api/Account/Ping to keep the session warm or test connectivity (uses Authorization header).
 * - getToken(): Returns token from memory if available, otherwise from sessionStorage.
 * - setMyUser(user): Persists user profile and role in sessionStorage and notifies subscribers via myUser$.
 * - isUserLoggedIn$/myUser$: Subscribe to react to session changes in the UI.
 *
 * Changes made (OWIN-only Bearer alignment)
 * - Token rehydration: On service construction, read 'rpmsToken' from sessionStorage so a page refresh keeps the session.
 * - Header helper: Added authHeaders() to unify building 'Authorization: Bearer <token>' and optional headers.
 * - POST shapes: Ensured logout() and ping() call HttpClient.post with null body and pass headers via options (correct signature).
 * - setToken(): Accepts a raw string or an OAuth response shape ({ access_token | token }) and persists only the token string.
 * - No functionality removed; existing storage keys (rpmsToken, rpmsUser, rpmsUserName, rpmsUserFullName, rpmsUserRole) retained.
 * - doOnLogout(): Closes dialogs, clears sensitive storage, updates state, navigates to /login, and forces a full reload.
 *
 * Security note
 * - Token is stored in sessionStorage (cleared on logout and by browser session end). This is consistent with existing app behavior.
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs/internal/Subject';
import { environment } from 'src/environments/environment';
import { MessageService } from 'src/app/shared/message.service';
import { BehaviorSubject } from 'rxjs';
import { AppConfig } from 'src/app/app.config';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  // NOTE (OWIN-only migration):
  // We keep your existing config approach (AppConfig.settings) and your original environment import.
  // No functionality removed here.
  private config = AppConfig.settings;

  //private _baseUrl = environment.baseUrl; //old way before config file in assets folder
  private _baseUrl = AppConfig.settings.baseUrl;

  // Tracks session state; unchanged.
  isUserLoggedIn: boolean = false;
  myUser: any;
  userRole;
  token: any;

  isUserLoggedIn$: Observable<any>;
  private _isUserLoggedInSubject = new BehaviorSubject<any>(this.isUserLoggedIn);
  myUser$: Observable<any>;
  private _myUserSubject = new Subject<any>();

  constructor(
    private _http: HttpClient,
    private _router: Router,
    private _messageService: MessageService,
    private dialogRef: MatDialog,
    //private _signalRService: SignalRService,
  ) {
    this.isUserLoggedIn$ = this._isUserLoggedInSubject.asObservable();
    this.myUser$ = this._myUserSubject.asObservable();

    // OWIN-only change:
    // Rehydrate token from sessionStorage on page refresh so API calls keep working.
    const token = sessionStorage.getItem('rpmsToken');
    if (token) this.token = token;

    this.setIsUserLoggedIn(token != null);

    // Preserve original user bootstrap from session storage.
    var user = JSON.parse(sessionStorage.getItem('rpmsUser'));
    if (user && !this.isEmpty(user)) this.setMyUser(user);
  }

  getConfig() {
    // Preserved: returns your settings; matches previous behavior.
    return this.config();
  }

  // LOGIN: unchanged flow to POST /Token, with URL encoding for safety.
  login(loginForm) {
    // OWIN Password grant (your server uses OAuthAuthorizationServerOptions at /Token)
    const data = "username=" + encodeURIComponent(loginForm.userName)
      + "&password=" + encodeURIComponent(loginForm.password)
      + "&grant_type=password";
    const reqHeader = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this._http.post(this._baseUrl + '/Token', data, { headers: reqHeader });
  }

  // LOGOUT: fixed HttpClient.post signature to ensure headers are sent as options and not as body.
  // Also includes Authorization: Bearer <token> so your OWIN bearer middleware authorizes the call.
  logout() {
    const reqHeader = this.authHeaders();
    this._http.post(this._baseUrl + '/api/Account/Logout', null, { headers: reqHeader })
      .subscribe(
        () => { this.doOnLogout(); },
        (response: HttpErrorResponse) => {
          // Preserved original behavior: treat specific error as successful logout
          if (response.error?.error === 'account_not_logged_in') {
            this.doOnLogout();
          }
          else {
            this._messageService.onFailure('Failed to log out.', response);
          }
        }
      );
  }

  // PING: fixed to include Authorization header for OWIN Bearer and to pass null body.
  ping() {
    const reqHeader = this.authHeaders();
    return this._http.post(this._baseUrl + '/api/Account/Ping', null, { headers: reqHeader });
  }

  // TOKEN STORAGE:
  // - Accept either the raw token string or an OAuth response object { access_token, ... }.
  // - Store just the token string in sessionStorage (preserves your storage pattern).
  setToken(data: any) {
    const token = typeof data === 'string'
      ? data
      : (data && (data.access_token || data.token));
    if (!token) return;

    this.token = token;
    sessionStorage.setItem('rpmsToken', token);
  }

  // Retrieve in-memory token first; fallback to sessionStorage (supports page refresh).
  getToken() {
    return this.token || sessionStorage.getItem('rpmsToken');
  }

  // Session state subject; preserved.
  setIsUserLoggedIn(data) {
    this.isUserLoggedIn = data;
    this._isUserLoggedInSubject.next(data);
  }

  getIsUserLoggedIn() {
    return this.isUserLoggedIn;
  }

  // User profile storage; preserved.
  setMyUser(data) {
    this.myUser = data;
    this.userRole = data.roles[0];
    sessionStorage.setItem('rpmsUser', JSON.stringify(data));
    sessionStorage.setItem('rpmsUserName', data.userName);
    sessionStorage.setItem('rpmsUserFullName', data.fullName);
    sessionStorage.setItem('rpmsUserRole', data.roles[0]);
    this._myUserSubject.next(data);
  }

  getMyUser() {
    return this.myUser;
  }

  getUserRole() {
    return this.userRole;
  }

  // LOGOUT CLEANUP: preserved logic and storage cleanup; unchanged behavior.
  doOnLogout() {
    this.dialogRef.closeAll();
    sessionStorage.removeItem('rpmsToken');
    sessionStorage.removeItem('rpmsUser');
    sessionStorage.removeItem('rpmsUserName');
    sessionStorage.removeItem('rpmsUserFullName');
    sessionStorage.removeItem('rpmsUserRole');
    localStorage.removeItem('rpmsSessionId');
    this.setIsUserLoggedIn(false);
    this._router.navigate(['/login']);
    window.location.reload();
  }

  // OWIN-only Bearer: attach Authorization header to API calls when a token is present.
  // Central helper to avoid duplicating header code in each request.
  private authHeaders(extra?: { [key: string]: string }): HttpHeaders {
    let headers = new HttpHeaders({ 'Accept': 'application/json' });
    const token = this.getToken();
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    if (extra) {
      Object.keys(extra).forEach(k => headers = headers.set(k, extra[k]));
    }
    return headers;
  }

  // Utility: preserved exactly as before.
  isEmpty(obj) {
    var hasOwnProperty = Object.prototype.hasOwnProperty;
    if (obj == null) return true;
    if (obj.length > 0) return false;
    if (obj.length === 0) return true;
    if (typeof obj !== "object") return true;
    for (var key in obj) {
      if (hasOwnProperty.call(obj, key)) return false;
    }
    return true;
  }
}
